/**
 * @file PictureArtwork.tsx
 * @description This file exports a React component named `PictureArtwork`. This component is responsible for rendering an artwork image with different resolutions based on the viewport size. It uses the HTML `picture` element to provide multiple sources for an image, allowing the browser to choose the most suitable one based on the current viewport size and file format support.
 */

/**
 * @typedef {Object} PictureArtworkProps
 * @property {string} imageCoreUrl - A string representing the base URL of the image.
 * @property {enum} artworkOrientation - An enum value (`ArtworkOrientation`) indicating the orientation of the artwork (horizontal or vertical).
 * @property {function} [onLoaded] - An optional function that is called when the image has finished loading.
 * @property {string} [loading='eager'] - An optional string that sets the loading behavior of the image. It can be either 'lazy' or 'eager'. The default value is 'eager'.
 * @property {number[]} breakpoints - An array of numbers representing the breakpoints for different viewport sizes.
 * @property {Object} artworkSets - An object (`ArtworkBreakPointConfig`) mapping viewport sizes to arrays of image resolutions.
 */

import {
  generalImageFallback,
  artworkPictureSourceSet,
} from '@/common/components/ArtworkPicture/artworkHelpers';
import { ArtworkBreakPointConfig } from '@/common/components/ArtworkPicture/artworksBreakpoints.config';
import { ImgExtension } from '@/common/types/Artwork/Artwork.type';
import { ArtworkOrientation } from 'generatedBfw/static';

import styles from './artworkPicture.module.scss';

interface PictureArtworkProps {
  imageCoreUrl: string;
  artworkOrientation: ArtworkOrientation;
  onLoaded?: () => void;
  loading?: 'lazy' | 'eager';
  artworkSets: ArtworkBreakPointConfig;
  pinNoPin?: boolean;
  alt?: string;
}

export const ArtworkPicture = ({
  imageCoreUrl,
  artworkOrientation,
  onLoaded,
  loading = 'eager',
  artworkSets,
  pinNoPin = true,
  alt = 'artwork',
}: PictureArtworkProps) => {
  const imageFallback = generalImageFallback(imageCoreUrl, artworkOrientation);
  const extensions: ImgExtension[] = ['avif', 'webp', 'jpg'];
  const breakpoints = Object.keys(artworkSets).map((breakpoint) => Number(breakpoint));

  return (
    <picture className={styles.artworkPicture}>
      {breakpoints
        .reverse()
        .map((breakpoint) =>
          extensions.map((ext) => (
            <source
              data-testid="artwork-source"
              key={`${breakpoint}-${ext}`}
              media={`(min-width: ${breakpoint}px)`}
              srcSet={`${artworkPictureSourceSet(
                imageCoreUrl,
                artworkOrientation,
                ext,
                breakpoint,
                artworkSets,
              )}`}
              type={`image/${ext}`}
            ></source>
          )),
        )}
      <img
        alt={alt}
        className={styles.artworkPicture}
        data-pin-nopin={pinNoPin}
        data-testid="artwork-img"
        draggable="false"
        loading={loading}
        onLoad={onLoaded}
        src={imageFallback}
      />
    </picture>
  );
};
